import type { TryOnApp } from '../app/TryOnApp';
import { SessionMeasure } from './SessionMeasure';
import './phone.css';

/** Opt-in test UI, kept outside the app so model reloads remain measurable. */
export class PhonePanel {
  private app: TryOnApp | null = null;
  private readonly run = new SessionMeasure();
  private pending: { start: number; kind: string; model: string } | null = null;
  private readonly lines: string[] = ['Maison Mata · A110Z4 · mesures locales (aucune image)'];
  private runLabel = '';
  private qualities = new Set<string>();
  private observations = 0;
  private firstSecondsLogged = false;
  private readonly root = document.createElement('details');
  private readonly live = document.createElement('p');
  private readonly status = document.createElement('p');
  private readonly report = document.createElement('textarea');
  private readonly optics = document.createElement('button');
  private readonly edgeAA = document.createElement('button');
  private readonly timer: number;
  constructor() {
    this.root.className = 'mt-phone-measure';
    this.root.open = true;
    const summary = document.createElement('summary'); summary.textContent = 'Test téléphone · A110Z4';
    const body = document.createElement('div');
    const note = document.createElement('p');
    note.textContent = 'Ouvrez TRY-ON. Mesures sans photo, conservées seulement dans cet onglet. Réduisez ce panneau pour voir votre visage.';
    this.optics.textContent = 'Verres physiques : activés';
    this.optics.onclick = () => {
      if (!this.app) return;
      this.abort('Changement du mode verres');
      this.app.setPhysicalLenses(!this.app.getMeasurement().physicalLenses);
    };
    const edgeAA = this.edgeAA;
    edgeAA.textContent = 'Bords fins : comparer';
    edgeAA.onclick = () => {
      if (!this.app) return;
      this.abort('Comparaison anticrénelage');
      const enabled = !this.app.getMeasurement().edgeAATest;
      this.app.setEdgeAATest(enabled);
      edgeAA.textContent = enabled ? 'Bords fins : activés (test)' : 'Bords fins : comparer';
      this.log(`Anticrénelage renforcé : ${enabled ? 'ON (coût GPU à mesurer)' : 'OFF'}`);
    };
    const controls = document.createElement('div'); controls.className = 'mt-phone-actions';
    for (const [label, seconds] of [['FPS · 30 s',30], ['Disparitions · 2 min',120], ['Chauffe · 3 min',180]] as const) {
      const b = document.createElement('button'); b.textContent = label;
      b.onclick = () => {
        const sample = this.app?.getMeasurement();
        if (!sample?.visible) { this.status.textContent = 'Attendez que la monture soit visible, puis recommencez.'; return; }
        this.abort('Nouvelle mesure'); this.observations = 0;
        this.runLabel = `${label} · verres ${sample.physicalLenses ? 'ON' : 'OFF'}`;
        this.qualities = new Set([sample.quality]);
        this.run.begin(performance.now(), seconds, sample);
      };
      controls.append(b);
    }
    const missed = document.createElement('button'); missed.textContent = 'J’ai vu une disparition';
    missed.onclick = () => { if (this.run.active) { this.observations++; this.status.textContent = `Disparitions vues : ${this.observations}`; } };
    const heat = document.createElement('select'); heat.setAttribute('aria-label','Chauffe ressentie');
    for (const label of ['Chauffe ressentie…','Aucune','Légère','Forte']) { const o = document.createElement('option'); o.textContent = label; heat.append(o); }
    heat.onchange = () => { if (heat.selectedIndex) this.log(`Chauffe déclarée : ${heat.value}`); };
    const device = document.createElement('input'); device.placeholder = 'Téléphone et navigateur (ex. iPhone 13 / Safari)'; device.setAttribute('aria-label','Téléphone et navigateur');
    device.onchange = () => this.log(`Appareil déclaré : ${device.value}`);
    this.report.readOnly = true; this.report.setAttribute('aria-label','Résultats du test'); this.report.rows = 5;
    const download = document.createElement('button'); download.textContent = 'Télécharger les résultats';
    download.onclick = () => {
      const url = URL.createObjectURL(new Blob([this.report.value], {type:'text/plain;charset=utf-8'}));
      const a = document.createElement('a'); a.href = url; a.download = 'mata-A110Z4-test-telephone.txt'; a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    body.append(note,device,this.live,this.optics,edgeAA,controls,missed,heat,this.status,this.report,download);
    this.root.append(summary,body); document.body.append(this.root);
    this.report.value = this.lines.join('\n');
    this.timer = window.setInterval(() => this.tick(), 250);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pagehide', () => { clearInterval(this.timer); document.removeEventListener('visibilitychange', this.onVisibility); }, {once:true});
  }
  begin(kind: 'Ouverture' | 'Changement', model: string): void {
    this.abort('Changement de session');
    if (this.pending) this.log(`${this.pending.kind} ${this.pending.model} : interrompu avant affichage`);
    this.pending = { start: performance.now(), kind, model };
    if (kind === 'Ouverture') this.firstSecondsLogged = false;
    this.app = null;
  }
  cancelPending(): void {
    if (this.pending) this.log(`${this.pending.kind} ${this.pending.model} : non terminé`);
    this.pending = null;
  }
  attach(app: TryOnApp): void { this.app = app; this.edgeAA.textContent = app.getMeasurement().edgeAATest ? 'Bords fins : activés (test)' : 'Bords fins : comparer'; }
  closed(app: TryOnApp): void {
    if (this.app !== app) return;
    this.abort('Fermeture');
    if (this.pending) this.log(`${this.pending.kind} : interrompu avant affichage`);
    this.pending = null; this.app = null;
  }
  private log(message: string): void {
    this.lines.push(message); if (this.lines.length > 100) this.lines.splice(1,1);
    this.report.value = this.lines.join('\n');
  }
  private abort(reason: string): void {
    if (this.run.active) { this.run.cancel(); this.log(`${this.runLabel} : annulé (${reason})`); }
  }
  private readonly onVisibility = () => {
    if (!document.hidden) return;
    this.abort('Onglet masqué / téléphone verrouillé');
    if (this.pending) { this.log(`${this.pending.kind} : annulé (onglet masqué)`); this.pending = null; }
  };
  private tick(): void {
    if (document.hidden) return;
    const sample = this.app?.getMeasurement();
    if (!sample) { this.live.textContent = 'En attente de TRY-ON.'; return; }
    const now = performance.now();
    if (this.pending && sample.firstVisibleAt !== null) {
      this.log(`${this.pending.kind} ${this.pending.model} : ${((sample.firstVisibleAt-this.pending.start)/1000).toFixed(2)} s (autorisation caméra incluse si demandée)`);
      if (this.pending.kind === 'Ouverture') {
        const t = sample.startup;
        this.log(`Démarrage : caméra ${(t.cameraMs/1000).toFixed(2)} s ; suivi ${(t.trackerMs/1000).toFixed(2)} s ; monture + photos ${(t.frameMs/1000).toFixed(2)} s. Caméra et suivi se chevauchent.`);
      }
      this.pending = null;
    }
    if (!this.firstSecondsLogged && sample.firstSeconds) {
      // A110W: the discomfort reported on phones (first seconds), measured on the phone itself.
      const f = sample.firstSeconds;
      this.log(`Premières 5 s (image caméra affichée) : ${f.fps.toFixed(0)} images/s ; saccades > 100 ms : ${f.over100} (> 50 ms : ${f.over50}) ; pire ${Math.round(f.worstMs)} ms ; changements de qualité : ${f.qualityChanges}${sample.preparedMs !== null ? ` ; monture prête ${(sample.preparedMs / 1000).toFixed(2)} s après l’image` : ''}`);
      this.firstSecondsLogged = true;
    }
    this.optics.textContent = `Verres physiques : ${sample.physicalLenses ? 'activés' : 'désactivés'}`;
    const active = this.run.active;
    const fps = this.run.sample(now,sample);
    this.live.textContent = `Rendus 3D : ${fps === null ? '—' : fps.toFixed(0)}/s · analyses : ${sample.inferenceFps}/s · caméra demandée : ${sample.cameraFps ?? '—'}/s · qualité ${sample.quality}`;
    if (active) this.qualities.add(sample.quality);
    if (this.run.active) this.status.textContent = `${this.run.remaining(now)} s restantes · interruptions détectées : ${this.run.interruptions} · vues : ${this.observations}`;
    else if (active && this.run.result) {
      const r = this.run.result;
      this.log(`${this.runLabel} : ${r.renderFps.toFixed(1)} rendus/s sur ${r.seconds.toFixed(1)} s ; interruptions détectées ${r.interruptions}, vues ${this.observations}, pertes moteur ${r.losses} ; qualité ${[...this.qualities].join(' → ')}`);
      this.status.textContent = 'Mesure terminée. Après le test de 3 minutes, indiquez la chauffe.';
    }
  }
}
